import React, { useState, useEffect } from 'react';
import './HRFlagged.css';

const BASE = 'https://fotileams.com/api';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
}

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarColor(name) {
  const colors = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#f97316'];
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
}

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-PK', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

const DEFAULT_MSG_EN = "Warning: Please do not attempt to clock in from outside the office. This is against company policy, and repeated violations will result in a heavy fine and disciplinary action. This is your final warning.";
const DEFAULT_MSG_UR = "انتباہ: براہ کرم دفتر سے باہر سے حاضری نہ لگائیں۔ یہ کمپنی کی پالیسی کے خلاف ہے، اور بار بار ایسا کرنے پر بھاری جرمانہ اور کارروائی کی جائے گی۔ یہ آپ کی آخری وارننگ ہے۔";

export default function HRFlagged() {
  const [flagged, setFlagged]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('unreviewed'); // 'unreviewed' | 'all'
  const [warnModal, setWarnModal] = useState(null); // the flagged record being warned
  const [msgEn, setMsgEn]         = useState(DEFAULT_MSG_EN);
  const [msgUr, setMsgUr]         = useState(DEFAULT_MSG_UR);
  const [sending, setSending]     = useState(false);
  const [actioning, setActioning] = useState(null); // id currently being marked absent/dismissed

  useEffect(() => { fetchFlagged(); }, [filter]);

  const fetchFlagged = async () => {
    try {
      setLoading(true);
      const reviewedParam = filter === 'unreviewed' ? '?reviewed=false' : '';
      const res  = await fetch(`${BASE}/attendance/flagged${reviewedParam}`, { headers: authHeaders() });
      const data = await res.json();
      setFlagged(data.flagged || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openWarnModal = (record) => {
    setWarnModal(record);
    setMsgEn(DEFAULT_MSG_EN);
    setMsgUr(DEFAULT_MSG_UR);
  };

  const sendWarning = async () => {
    if (!warnModal) return;
    setSending(true);
    try {
      await fetch(`${BASE}/warnings/send`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          employee_id: warnModal.employee_id,
          attendance_id: warnModal.id,
          message_en: msgEn,
          message_ur: msgUr,
        }),
      });
      setWarnModal(null);
      fetchFlagged();
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const markAbsent = async (record) => {
    if (!window.confirm(`Mark ${record.full_name} as ABSENT for ${new Date(record.date).toLocaleDateString('en-PK', { day: 'numeric', month: 'short' })}? This will affect their salary.`)) return;
    setActioning(record.id);
    try {
      await fetch(`${BASE}/attendance/${record.id}/mark-absent`, { method: 'PUT', headers: authHeaders() });
      fetchFlagged();
    } catch (err) {
      console.error(err);
    } finally {
      setActioning(null);
    }
  };

  const dismissFlag = async (record) => {
    setActioning(record.id);
    try {
      await fetch(`${BASE}/attendance/${record.id}/dismiss-flag`, { method: 'PUT', headers: authHeaders() });
      fetchFlagged();
    } catch (err) {
      console.error(err);
    } finally {
      setActioning(null);
    }
  };

  const getSeverity = (score) => {
    if (score >= 70) return { label: 'High Risk',   color: '#ef4444' };
    if (score >= 45) return { label: 'Medium Risk', color: '#f59e0b' };
    return                  { label: 'Low Risk',    color: '#3b82f6' };
  };

  const unreviewedCount = flagged.filter(f => !f.spoof_reviewed).length;

  return (
    <div className="fl-root">
      <div className="fl-header">
        <div>
          <h1 className="fl-title">🚩 Flagged Attendance</h1>
          <p className="fl-sub">GPS clock-ins the system flagged as potentially suspicious — you decide what happens next</p>
        </div>
        <div className="fl-filter-pills">
          <button className={`fl-pill ${filter === 'unreviewed' ? 'fl-pill-active' : ''}`}
            onClick={() => setFilter('unreviewed')}>
            Needs Review {unreviewedCount > 0 && <span className="fl-pill-badge">{unreviewedCount}</span>}
          </button>
          <button className={`fl-pill ${filter === 'all' ? 'fl-pill-active' : ''}`}
            onClick={() => setFilter('all')}>All History</button>
        </div>
      </div>

      {loading ? (
        <div className="fl-loading"><div className="fl-spinner" /></div>
      ) : flagged.length === 0 ? (
        <div className="fl-empty">
          <div className="fl-empty-icon">✓</div>
          <div className="fl-empty-title">All Clear</div>
          <div className="fl-empty-sub">No suspicious clock-ins to review right now</div>
        </div>
      ) : (
        <div className="fl-list">
          {flagged.map((r) => {
            const severity = getSeverity(r.spoof_flag_score);
            const isActing = actioning === r.id;
            return (
              <div key={r.id} className="fl-card" style={{ borderLeftColor: severity.color }}>
                <div className="fl-card-top">
                  <div className="fl-emp-row">
                    <div className="fl-avatar" style={{ background: getAvatarColor(r.full_name) }}>
                      {getInitials(r.full_name)}
                    </div>
                    <div>
                      <div className="fl-emp-name">{r.full_name}</div>
                      <div className="fl-emp-meta">{r.emp_code} · {r.department || 'Unassigned'} · {r.location_name}, {r.city}</div>
                    </div>
                  </div>
                  <div className="fl-severity" style={{ background: `${severity.color}1a`, color: severity.color }}>
                    {severity.label} · {r.spoof_flag_score} pts
                  </div>
                </div>

                <div className="fl-card-meta-row">
                  <span className="fl-meta-item">📅 {new Date(r.date).toLocaleDateString('en-PK', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                  <span className="fl-meta-item">🕐 Clocked in {formatDateTime(r.clock_in)}</span>
                  {r.spoof_reviewed && <span className="fl-reviewed-tag">✓ Reviewed</span>}
                </div>

                <div className="fl-reasons">
                  {(r.spoof_flag_reasons || []).map((reason, i) => (
                    <div key={i} className="fl-reason-item">⚠ {reason}</div>
                  ))}
                </div>

                {!r.spoof_reviewed && (
                  <div className="fl-actions">
                    <button className="fl-btn fl-btn-warn" onClick={() => openWarnModal(r)}>
                      ⚠️ Send Warning
                    </button>
                    <button className="fl-btn fl-btn-absent" disabled={isActing} onClick={() => markAbsent(r)}>
                      {isActing ? '...' : '✗ Mark Absent'}
                    </button>
                    <button className="fl-btn fl-btn-dismiss" disabled={isActing} onClick={() => dismissFlag(r)}>
                      Dismiss (Looks Fine)
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Warning Modal */}
      {warnModal && (
        <div className="fl-modal-overlay" onClick={() => setWarnModal(null)}>
          <div className="fl-modal" onClick={e => e.stopPropagation()}>
            <div className="fl-modal-header">
              <div>
                <div className="fl-modal-title">Send Warning</div>
                <div className="fl-modal-sub">To {warnModal.full_name} ({warnModal.emp_code})</div>
              </div>
              <button className="fl-modal-close" onClick={() => setWarnModal(null)}>✕</button>
            </div>

            <div className="fl-modal-body">
              <label className="fl-modal-label">🇬🇧 English Message</label>
              <textarea className="fl-modal-textarea" rows="4" value={msgEn} onChange={e => setMsgEn(e.target.value)} />

              <label className="fl-modal-label" style={{ marginTop: 14 }}>🇵🇰 اردو پیغام</label>
              <textarea className="fl-modal-textarea fl-textarea-urdu" rows="4" dir="rtl" value={msgUr} onChange={e => setMsgUr(e.target.value)} />

              <div className="fl-modal-note">
                This will appear as a popup the employee must acknowledge next time they open the app.
              </div>
            </div>

            <div className="fl-modal-footer">
              <button className="fl-modal-cancel" onClick={() => setWarnModal(null)}>Cancel</button>
              <button className="fl-modal-send" onClick={sendWarning} disabled={sending}>
                {sending ? 'Sending…' : 'Send Warning'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}